import { useState, useEffect } from "react";
import { Eye, EyeSlash, Copy, Check } from "@phosphor-icons/react";
import CollapsibleSection from "@/components/admin/CollapsibleSection";
import { isNoTrack, setNoTrack, getVisitorId } from "@/lib/analyticsClient";

/**
 * Simple toggle so the person running the site can exclude their own visits
 * from analytics. Stored in localStorage — per browser, per device. Also shows
 * a link the user can send to themselves on other devices (?sws_no_track=1).
 */
export default function ExcludeDevicePanel({ defaultOpen = true }) {
  const [excluded, setExcluded] = useState(false);
  const [vid, setVid] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setExcluded(isNoTrack());
    setVid(getVisitorId());
  }, []);

  const toggle = () => {
    const next = !excluded;
    setNoTrack(next);
    setExcluded(next);
  };

  const excludeLink = typeof window !== "undefined"
    ? `${window.location.origin}/?sws_no_track=1`
    : "https://sweatwithslay.com/?sws_no_track=1";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(excludeLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  };

  return (
    <CollapsibleSection
      id="exclude-device"
      testId="admin-exclude-device"
      title={<>{excluded
        ? <><EyeSlash size={18} weight="fill" className="text-[#FF1B91] inline mr-2" />This device is excluded</>
        : <><Eye size={18} weight="fill" className="text-white/70 inline mr-2" />Exclude my device</>
      }</>}
      subtitle={excluded ? "Your visits won’t appear in analytics." : "Turn on so your own testing doesn’t inflate the numbers."}
      defaultOpen={defaultOpen}
    >
      <div className="border border-white/10 bg-[#0F0F0F] p-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="font-display font-black uppercase text-base tracking-tight mb-1 text-white">
              {excluded ? "Tracking OFF on this browser" : "Tracking ON — your visits count"}
            </div>
            <div className="text-white/60 text-sm max-w-lg">
              This is stored in this browser only. You need to toggle it on every device / browser you personally use
              (laptop, phone, incognito, etc.).
            </div>
          </div>
          <button
            data-testid="toggle-exclude-device"
            onClick={toggle}
            className={`text-[11px] uppercase tracking-[0.25em] px-6 py-3 border-2 font-bold transition-all ${
              excluded
                ? "border-[#FF1B91] bg-[#FF1B91] text-white hover:bg-[#FF1B91]/80"
                : "border-white/25 text-white/85 hover:border-white/50"
            }`}
          >
            {excluded ? "Include me again" : "Exclude this device"}
          </button>
        </div>

        <div className="mt-5 pt-5 border-t border-white/10">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 font-bold mb-2">Exclude another device</div>
          <p className="text-white/60 text-sm mb-3">
            Open this link on any other browser you use — phone, tablet, second laptop — and it will auto-exclude that device from analytics forever.
          </p>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={excludeLink}
              className="flex-1 bg-black border border-white/20 text-white/85 px-3 py-2 text-xs font-mono"
              data-testid="exclude-link"
            />
            <button
              data-testid="copy-exclude-link"
              onClick={copyLink}
              className={`text-[11px] uppercase tracking-[0.25em] px-4 py-2 border-2 font-bold inline-flex items-center gap-2 ${copied ? "border-[#B32027] bg-[#B32027] text-white" : "border-white/25 text-white/85 hover:border-white/50"}`}
            >
              {copied ? <><Check size={13} weight="bold" /> Copied</> : <><Copy size={13} weight="bold" /> Copy</>}
            </button>
          </div>
        </div>

        {vid && (
          <div className="mt-4 text-[10px] uppercase tracking-[0.25em] text-white/35 font-mono">
            This device’s anonymous visitor id: {vid.slice(0, 8)}…{vid.slice(-4)}
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}
